import React, { useEffect, useState } from 'react';
import ImgThemed from '@/components/ImgThemed/ImgThemed';
import { BladeModal } from '@monavate/ui-core';
import { KeyValueViewer } from '@monavate/ui-core';
import {
    create3DSecureObject,
    createAcquirerObject,
    createAmountObject,
    createPaymentObject,
    createPOSObject,
    createTransactionObject,
    hasDataToDisplayInKeyValueComponent,
} from '@/components/TransactionDetailModal/helper';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '@/reduxStore/rootReducer';
import {
    closeTransactionModal,
    setTransactionDetailToDisplay,
} from '@/reduxStore/slices/transactionsSlice';
import { TabItemType } from '@/components/TabSwitcher/helper';
import styled from 'styled-components';
import { TabSwitcher } from '@monavate/ui-core';
import { Divider } from '@monavate/ui-core';
import SupplierSection from './components/SupplierSection/SupplierSection';
import RelatedTransactions from '@/components/TransactionDetailModal/components/RelatedTransactions/RelatedTransactions';
import ArrowRight from '@/assets/icons/arrow-right.svg';
import { Button } from '@monavate/ui-core';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { CardResponse } from '../../api/general';
import { cardsApi, paymentApi } from '../../services/rest/apiInstances';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    AccordionSummaryText,
} from '@monavate/ui-core';
import { buildClientApiConfig } from '@/helpers/apiRequestConfig';

const ButtonContainer = styled.div`
    width: 20%;
    display: flex;
    align-items: center;
    justify-content: center;

    & button {
        height: 32px;
        width: 32px;
        padding: 4px;
    }

    & img {
        rotate: 180deg;
    }
`;

const DetailsContainer = styled.div`
    background: ${(props) => props.theme['Surface/page']}
        /* TODO: check color. Alternatives: ["Surface/page","Surface/primary","Content/inverse","Content/overlay"] */;
`;

interface BladeModalHeaderProps {
    related?: boolean;
    handleClick?: () => void;
    tabBarItems: TabItemType[];
    transactionDetailToDisplay: any;
    relatedTransaction: any;
    activeTab: number;
}

const Header = styled.div<{ related?: boolean }>`
    display: flex;
    flex-direction: ${(props) => (props.related ? 'row' : 'column')};
    align-items: center;
    padding: ${(props) => (props.related ? '32px 32px 24px 0' : '32px 32px 0')};
    background: ${(props) => props.theme['Surface/secondary']};
    font-family: 'Poppins', serif;
    border-bottom: 1px solid ${(props) => props.theme['Borders/quiet']};
`;

const TabContainer = styled.div`
    width: 100%;
`;

const PaddingContainer = styled.div`
    padding: 0 32px;
`;

const MainTitle = styled.div<{ related: boolean }>`
    color: ${(props) => props.theme['Content/primary']}
        /* TODO: check color. Alternatives: ["Content/primary","Content/logo","Content/on-accent"] */;
    font-family: 'Poppins', serif;
    font-size: 16px;
    width: 100%;
    margin-bottom: ${(props) => (props.related ? '0' : '24px')};
    font-style: normal;
    font-weight: 600;
    line-height: 24px;
`;

const BladeModalHeader = ({
    related,
    handleClick,
    transactionDetailToDisplay,
    relatedTransaction,
    tabBarItems,
    activeTab,
}: BladeModalHeaderProps) => {
    return (
        <Header related={related}>
            {related && (
                <ButtonContainer>
                    <Button
                        hideBorder
                        style={{ background: 'transparent' }}
                        handleClick={handleClick}
                    >
                        <ImgThemed src={ArrowRight} alt="Arrow" />
                    </Button>
                </ButtonContainer>
            )}
            <MainTitle related={!!related}>
                {related ? 'Related transactions' : 'Transaction details'}
            </MainTitle>
            {transactionDetailToDisplay?.relatedTransactionIds && !relatedTransaction && (
                <TabContainer>
                    <TabSwitcher
                        items={tabBarItems}
                        activeItemIndex={activeTab}
                        isInsideBladeModal
                    />
                </TabContainer>
            )}
        </Header>
    );
};

interface TransactionDetailModalProps {
    isOpen: boolean;
}

const TransactionDetailModal: React.FC<TransactionDetailModalProps> = ({ isOpen }) => {
    const dispatch = useDispatch();
    const [activeTab, setActiveTab] = useState(0);
    const [relatedTransaction, setRelatedTransaction] = useState<any>(null);
    const [currentTransaction, setCurrentTransaction] = useState<any>(null);
    const isOpenTransactionDetailModal = useSelector(
        (state: RootState) => state.transaction.isOpenTransactionDetailModal,
    );
    const transactionDetailToDisplay = useSelector(
        (state: RootState) => state.transaction.transactionDetailToDisplay,
    );
    const relatedTransactionsList = useSelector(
        (state: RootState) => state.transaction.relatedTransactionsList,
    );
    const [paymentInfo, setPaymentInfo] = useState<any>();
    const [cardData, setCardData] = useState<CardResponse | undefined>();
    const isCardTransaction = transactionDetailToDisplay?.type === 'card';

    const getCardData = async (cardId: string) => {
        try {
            const options = buildClientApiConfig();
            const cardData = await cardsApi.getCard({ cardId: cardId }, options);
            if (cardData.data) {
                setCardData(cardData.data);
            }
        } catch (e: any) {
            console.log(e);
        }
    };
    const getPaymentInstructionData = async (paymentId: string) => {
        try {
            const options = buildClientApiConfig();
            const paymentData = await paymentApi.getPayment({ paymentId }, options);
            const data = paymentData?.data;
            if (paymentData?.data) {
                setPaymentInfo(data);
            }
            const cardId =
                // @ts-ignore
                paymentData.data?.card?.id || transactionDetailToDisplay.cardTransaction.cardId;
            if (cardId) {
                getCardData(cardId);
            }
        } catch (e: any) {
            console.log(e);
        }
    };
    useEffect(() => {
        if (transactionDetailToDisplay.paymentId) {
            getPaymentInstructionData(transactionDetailToDisplay.paymentId);
        }
        if (
            isCardTransaction &&
            !transactionDetailToDisplay.paymentId &&
            !!transactionDetailToDisplay.cardTransaction?.cardId
        ) {
            getCardData(transactionDetailToDisplay.cardTransaction.cardId);
        }
    }, [transactionDetailToDisplay]);

    const onCloseModalHandler = () => {
        dispatch(closeTransactionModal());
    };

    const tabBarItems: TabItemType[] = [
        {
            label: 'Details',
            callback: () => setActiveTab(0),
        },
        ...(relatedTransactionsList?.length > 0
            ? [
                  {
                      label: 'Related transactions',
                      callback: () => setActiveTab(1),
                  },
              ]
            : []),
    ];

    useEffect(() => {
        if (relatedTransaction) {
            dispatch(setTransactionDetailToDisplay(relatedTransaction));
        }
    }, [relatedTransaction]);

    useEffect(() => {
        if (transactionDetailToDisplay) {
            setCurrentTransaction(transactionDetailToDisplay);
        }
    }, [isOpenTransactionDetailModal]);

    const backButtonHandler = () => {
        setRelatedTransaction(null);
        dispatch(setTransactionDetailToDisplay(currentTransaction));
    };
    return (
        <BladeModal
            isOpen={isOpen}
            closeButton={true}
            handleClose={onCloseModalHandler}
            padding={'0'}
            customHeader={
                <BladeModalHeader
                    related={relatedTransaction}
                    handleClick={backButtonHandler}
                    relatedTransaction={relatedTransaction}
                    transactionDetailToDisplay={transactionDetailToDisplay}
                    tabBarItems={tabBarItems?.length > 1 ? tabBarItems : []}
                    activeTab={activeTab}
                />
            }
        >
            <div>
                {(relatedTransaction || activeTab == 0) && transactionDetailToDisplay && (
                    <DetailsContainer>
                        {hasDataToDisplayInKeyValueComponent(
                            createTransactionObject(transactionDetailToDisplay),
                        ) && (
                            <Accordion defaultExpanded>
                                <AccordionSummary
                                    expandIcon={<ExpandMoreIcon />}
                                    aria-controls="transaction-content"
                                    id="transaction-header"
                                >
                                    <AccordionSummaryText>Transaction</AccordionSummaryText>
                                </AccordionSummary>
                                <AccordionDetails>
                                    <KeyValueViewer
                                        data={createTransactionObject(transactionDetailToDisplay)}
                                    />
                                </AccordionDetails>
                            </Accordion>
                        )}
                        {hasDataToDisplayInKeyValueComponent(
                            createPaymentObject(transactionDetailToDisplay, paymentInfo, cardData),
                        ) && (
                            <Accordion defaultExpanded>
                                <AccordionSummary
                                    expandIcon={<ExpandMoreIcon />}
                                    aria-controls="payment-content"
                                    id="payment-header"
                                >
                                    <AccordionSummaryText>Payment</AccordionSummaryText>
                                </AccordionSummary>
                                <AccordionDetails>
                                    <KeyValueViewer
                                        data={createPaymentObject(
                                            transactionDetailToDisplay,
                                            paymentInfo,
                                            cardData,
                                        )}
                                    />
                                </AccordionDetails>
                            </Accordion>
                        )}
                        <SupplierSection paymentInfo={paymentInfo} />

                        {isCardTransaction && (
                            <>
                                {hasDataToDisplayInKeyValueComponent(
                                    createAmountObject(transactionDetailToDisplay),
                                ) && (
                                    <Accordion defaultExpanded>
                                        <AccordionSummary
                                            expandIcon={<ExpandMoreIcon />}
                                            aria-controls="amount-content"
                                            id="amount-header"
                                        >
                                            <AccordionSummaryText>Amount</AccordionSummaryText>
                                        </AccordionSummary>
                                        <AccordionDetails>
                                            <KeyValueViewer
                                                data={createAmountObject(
                                                    transactionDetailToDisplay,
                                                )}
                                            />
                                        </AccordionDetails>
                                    </Accordion>
                                )}
                                {hasDataToDisplayInKeyValueComponent(
                                    create3DSecureObject(transactionDetailToDisplay),
                                ) && (
                                    <Accordion defaultExpanded>
                                        <AccordionSummary
                                            expandIcon={<ExpandMoreIcon />}
                                            aria-controls="3dsecure-content"
                                            id="3dsecure-header"
                                        >
                                            <AccordionSummaryText>3D secure</AccordionSummaryText>
                                        </AccordionSummary>
                                        <AccordionDetails>
                                            <KeyValueViewer
                                                data={create3DSecureObject(
                                                    transactionDetailToDisplay,
                                                )}
                                            />
                                        </AccordionDetails>
                                    </Accordion>
                                )}

                                {hasDataToDisplayInKeyValueComponent(
                                    createAcquirerObject(transactionDetailToDisplay),
                                ) && (
                                    <Accordion defaultExpanded>
                                        <AccordionSummary
                                            expandIcon={<ExpandMoreIcon />}
                                            aria-controls="acquirer-content"
                                            id="acquirer-header"
                                        >
                                            <AccordionSummaryText>Acquirer</AccordionSummaryText>
                                        </AccordionSummary>
                                        <AccordionDetails>
                                            <KeyValueViewer
                                                data={createAcquirerObject(
                                                    transactionDetailToDisplay,
                                                )}
                                            />
                                        </AccordionDetails>
                                    </Accordion>
                                )}
                                {hasDataToDisplayInKeyValueComponent(
                                    createPOSObject(transactionDetailToDisplay),
                                ) && (
                                    <Accordion defaultExpanded>
                                        <AccordionSummary
                                            expandIcon={<ExpandMoreIcon />}
                                            aria-controls="pos-content"
                                            id="pos-header"
                                        >
                                            <AccordionSummaryText>POS</AccordionSummaryText>
                                        </AccordionSummary>
                                        <AccordionDetails>
                                            <KeyValueViewer
                                                data={createPOSObject(transactionDetailToDisplay)}
                                            />
                                        </AccordionDetails>
                                    </Accordion>
                                )}
                            </>
                        )}
                    </DetailsContainer>
                )}
                {!relatedTransaction && activeTab == 1 && (
                    <>
                        <Divider />
                        <RelatedTransactions setRelatedTransaction={setRelatedTransaction} />
                    </>
                )}
            </div>
        </BladeModal>
    );
};

export default TransactionDetailModal;
